import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Guideline_Role_Enum,
    Guideline_Status_Enum,
    Guideline_Type_Enum,
    GuidelineFragment,
    Guidelines_Insert_Input,
    UpdateGuidelineMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    OperationsAdminVenueBookingGuidelineCreateFragment, 
    OperationsAdminVenueBookingGuidelineCreateSchema, 
    OperationsAdminVenueBookingGuidelineInfoFragment, 
    OperationsAdminVenueBookingGuidelineInfoSchema, 
    OperationsAdminVenueBookingGuidelineQuestionFragment, 
    OperationsAdminVenueBookingGuidelineQuestionSchema, 
    OperationsAdminVenueBookingGuidelineTaskFragment, 
    OperationsAdminVenueBookingGuidelineTaskSchema, 
    OperationsAdminVenueBookingGuidelineUpdateFragment, 
    OperationsAdminVenueBookingGuidelineUpdateSchema 
} from "src/components/operations/administrator/bookings/guidelines/OperationsAdminVenueBookingGuidelineSchemas"
import { extractCurrentItemData, FormSchema } from "src/utils/forms"
import { GuidelinesList } from "src/components/ui/GuidelinesList"
import { GuidelineMetaData } from "src/state/_types"

type FormPayloadType = OperationsAdminVenueBookingGuidelineCreateFragment

let initialState: FormPayloadType = {
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
};

export const CreateOperationsAdminVenueBookingGuidelineDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().guidelines;
    const { loading, errors } = useAppState().guidelines;
    const { currentItemId: currentBookingId } = useAppState().bookings;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Guidelines_Insert_Input) => {
        return createItem({ booking_id: currentBookingId, type: Guideline_Type_Enum.Booking, parent_guideline_type: Guideline_Type_Enum.Booking, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, GuidelineFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueBookingGuidelineCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateOperationsAdminVenueBookingGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Add Guideline'
            action={CreateOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: OperationsAdminVenueBookingGuidelineUpdateFragment = {
    status: Guideline_Status_Enum.Active,
    role: Guideline_Role_Enum.Info,
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
    tenant_confirmed: false,
    venue_confirmed: false,
    talent_confirmed: false,
    tenant_completed: false,
    venue_completed: false,
    talent_completed: false,    
    target_booking_type: null,
    target_booking_status: null,
};

export const UpdateOperationsAdminVenueBookingGuidelineDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: OperationsAdminVenueBookingGuidelineUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<OperationsAdminVenueBookingGuidelineUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<OperationsAdminVenueBookingGuidelineUpdateFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueBookingGuidelineUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateOperationsAdminVenueBookingGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditOperationsAdminVenueBookingGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}

type BookingGuidelineFragment = OperationsAdminVenueBookingGuidelineInfoFragment | OperationsAdminVenueBookingGuidelineTaskFragment | OperationsAdminVenueBookingGuidelineQuestionFragment;

const BookingGuidelineSchemaMap = {
    [Guideline_Role_Enum.Info]: OperationsAdminVenueBookingGuidelineInfoSchema,
    [Guideline_Role_Enum.AgencyTask]: OperationsAdminVenueBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.AreaTask]: OperationsAdminVenueBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.VenueTask]: OperationsAdminVenueBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.TalentTask]: OperationsAdminVenueBookingGuidelineTaskSchema,
    [Guideline_Role_Enum.TalentQuestion]: OperationsAdminVenueBookingGuidelineQuestionSchema,
    [Guideline_Role_Enum.AreaQuestion]: OperationsAdminVenueBookingGuidelineQuestionSchema,
    [Guideline_Role_Enum.VenueQuestion]: OperationsAdminVenueBookingGuidelineQuestionSchema,
}

export const ProcessOperationsAdminVenueBookingGuidelineDrawer: React.FC<{ onClose(): void, deselect: boolean, schema: FormSchema<BookingGuidelineFragment> }> = ({ onClose, deselect = true }) => {


    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    const currentSchema = React.useMemo(() => {
        return BookingGuidelineSchemaMap[currentItem?.role as Guideline_Role_Enum] || OperationsAdminVenueBookingGuidelineInfoSchema;
    }, [currentItem])

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: BookingGuidelineFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<BookingGuidelineFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if (deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<BookingGuidelineFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={currentSchema}
                validators={{
                    compare_fields: async (value: string, context: any) => {
                        let booleanValue = value === 'true';
                        return context.parent.venue_required ? booleanValue : true;
                    },
                    validate_metadata: async (value: string) => {
                        try {
                            let guidelineMetaData = JSON.parse(value) as GuidelineMetaData;
                            let unansweredQuestions = guidelineMetaData.questions.filter(q => q.required && !q.answer);
                            if (unansweredQuestions.length > 0) {
                                return false;
                            }
                            return true;
                        } catch (error) {
                            return true;
                        }
                    }
                }}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
                validateOnMount={true}
            />

        </>
    )

}

export const ProcessOperationsAdminVenueBookingGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Review Guideline'
            action={ProcessOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export function OperationsAdminVenueBookingGuidelineActions() {

    const { currentItem: currentBooking } = useAppState(state => state.bookings)
    const { itemsList: guidelines } = useAppState(state => state.guidelines)
    const { subscribeToItems, unsubscribeFromItems } = useActions().guidelines;

    React.useEffect(() => {
        if (currentBooking) {
            subscribeToItems({
                where: {
                    booking_id: { _eq: currentBooking.id },
                    status: { _eq: Guideline_Status_Enum.Active },
                    venue_required: { _eq: true },
                    _or: [
                        { target_booking_status: { _is_null: true } },
                        { target_booking_status: { _in: [currentBooking.status] } }
                    ]
                },
                limit: 1000
            })
            return () => {
                unsubscribeFromItems()
            }
        }
    }, [currentBooking, subscribeToItems, unsubscribeFromItems])

    const tasks = React.useMemo(() => {
        return guidelines.filter(g => g.role === Guideline_Role_Enum.AgencyTask || g.role === Guideline_Role_Enum.AreaTask || g.role === Guideline_Role_Enum.VenueTask || g.role === Guideline_Role_Enum.TalentTask);
    }, [guidelines])

    const questions = React.useMemo(() => {
        return guidelines.filter(g => g.role === Guideline_Role_Enum.AreaQuestion || g.role === Guideline_Role_Enum.VenueQuestion || g.role === Guideline_Role_Enum.TalentQuestion);
    }, [guidelines])

    const info = React.useMemo(() => {
        return guidelines.filter(g => g.role === Guideline_Role_Enum.Info);
    }, [guidelines])

    return (
        <>
            <ProcessOperationsAdminVenueBookingGuidelineAction />
            <GuidelinesList
                title="Tasks"
                icon="ph:list-checks-duotone"
                guidelines={tasks}
                track_venue={true}
            />
            <GuidelinesList
                title="Questions"
                icon="ph:question-duotone"
                guidelines={questions}
                track_venue={true}
            />
            <GuidelinesList
                title="Important Information"
                icon="ph:clipboard-text-duotone"
                guidelines={info}
                track_venue={true}
            />
        </>
    )

}